import { FunctionComponent, useEffect, useState } from 'react';
import {
  ChoiceChipset,
  ContentFormatter,
  Copy,
  PictureContainer,
  Section,
  InputsWrapper,
  AccordionRow,
  Column,
  Columns
} from '@belong/ui-components';
import { BREAKPOINTS } from '@belong/themes';
import { hasValue } from '@belong/type-utils';
import { ISectionCategoryFilter } from './model';
import { ImageWrapper } from './styles';

export const TEST_ID = {
  SECTION: 'section-category-filter'
};

const DESKTOP_BREAKPOINT = 'md';

// Desktop view: Show all content in full layout
const CategoryFilterDesktop: FunctionComponent<ISectionCategoryFilter> = props => {
  const {
    id,
    compressOnMobile,
    filterOptionLabel,
    identifier,
    image,
    onChange,
    optionGroupDescription,
    options = [],
    selectedValue,
    ...sectionProps
  } = props;
  return (
    <Section
      data-contentid={id}
      data-identifier={identifier}
      {...sectionProps}
      hasBackground="white"
      data-testid={TEST_ID.SECTION}
    >
      <Columns alignX="center">
        <Column width={{ md: '5/6', lg: '2/3', xl: '1/2' }}>
          <InputsWrapper>
            <ContentFormatter alignment="center">
              {image && (
                <ImageWrapper contentType={image.contentType}>
                  <PictureContainer {...image} src={[image.src, { fit: 'fill' }]} />
                </ImageWrapper>
              )}
              <Copy isBold>{filterOptionLabel}</Copy>
              <ChoiceChipset
                name="filter"
                options={options}
                hasControl={{ xs: ['row', 'left'] }}
                selectedValue={selectedValue}
                onChange={onChange}
                description={optionGroupDescription}
              />
            </ContentFormatter>
          </InputsWrapper>
        </Column>
      </Columns>
    </Section>
  );
};

// Mobile view: hide image and display options in accordion
const CategoryFilterMobile: FunctionComponent<ISectionCategoryFilter> = props => {
  const { identifier, filterOptionLabel, options, selectedValue, onChange, optionGroupDescription } = props;
  return (
    <Section
      data-identifier={identifier}
      data-testid={TEST_ID.SECTION}
      hasBackground="grey"
      hasPaddingTop={false}
      isFullWidth
      isPadded={false}
    >
      <AccordionRow label={filterOptionLabel} initialState="open">
        <ChoiceChipset
          key="options"
          name="filter"
          options={options}
          hasControl={{ xs: ['row', 'left'], [DESKTOP_BREAKPOINT]: ['row', 'center'] }}
          selectedValue={selectedValue}
          onChange={onChange}
          description={optionGroupDescription}
        />
      </AccordionRow>
    </Section>
  );
};

const SectionCategoryFilter: FunctionComponent<ISectionCategoryFilter> = ({ compressOnMobile, ...viewProps }) => {
  const [isDesktopView, setIsDesktopView] = useState<boolean | null>(null);
  const showDesktopView = isDesktopView || !compressOnMobile;
  const handleBreakpointChange = (event: MediaQueryListEvent) => {
    setIsDesktopView(event.matches);
  };

  useEffect(() => {
    const media = window.matchMedia(`(min-width: ${BREAKPOINTS[DESKTOP_BREAKPOINT] / 16}em)`);
    setIsDesktopView(media.matches);
    media.addListener(handleBreakpointChange);

    return (): void => {
      media.removeListener(handleBreakpointChange);
    };
  }, []);

  // don't render until we know which view is appropriate
  if (!hasValue(isDesktopView)) {
    return null;
  }

  const View = showDesktopView ? CategoryFilterDesktop : CategoryFilterMobile;
  return <View {...viewProps} />;
};

export default SectionCategoryFilter;
